import { type IconName } from '@bettermode/icons/social/types'
import {
  DefaultSsoType,
  SsoStatus,
  SsoType,
} from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useNetwork,
  useSsos,
  useUpdateDefaultSsoStatus,
  useUpdateNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SvgSocialIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { JwtSso } from './JwtSso.js'
import { Oauth2Sso } from './Oauth2Sso.js'
import { SamlSso } from './SamlSso.js'

export const Authentication = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutate: updateNetwork } = useUpdateNetwork()
  const { mutate: updateDefaultSsoStatus } = useUpdateDefaultSsoStatus()
  const { completeStep } = useAdminOnboarding()

  const { data: ssos } = useSsos()
  const icons: Record<string, IconName> = {
    linkedin: 'linked-in',
    facebook: 'facebook',
    google: 'google',
    discord: 'discord',
    slack: 'slack',
    apple: 'apple',
    twitter: 'twitter',
  }

  const defaultSsos = ssos?.filter(
    sso =>
      sso.type !== SsoType.oauth2 &&
      sso.type !== SsoType.Saml &&
      sso.type !== SsoType.jwt,
  )

  const oauth2Sso = ssos?.find(sso => sso.type === SsoType.oauth2)
  const jwtSso = ssos?.find(sso => sso.type === SsoType.jwt)
  const samlSso = ssos?.find(sso => sso.type === SsoType.Saml)
  const jwtSsoActive =
    jwtSso?.status === SsoStatus.enable &&
    (jwtSso?.authorizationUrl || jwtSso?.signUpUrl)

  const enabledSsosCount = ssos?.reduce((sum, sso) => {
    // jwt will be considered as active only if there is a url associated with the jwt
    if (sso.type === SsoType.jwt) {
      if (
        sso.status === SsoStatus.enable &&
        (sso.authorizationUrl || sso.signUpUrl)
      ) {
        sum += 1
      }
      return sum
    }
    if (sso.status === SsoStatus.enable) {
      sum += 1
    }
    return sum
  }, 0)
  const hideDefaultAuthenticationForm = network?.hideDefaultAuthenticationForm
  const activeLoginProviderCount =
    enabledSsosCount + (hideDefaultAuthenticationForm ? 0 : 1)

  // You can safely disable one login provider if there are at least two enabled
  const canSafelyDisableLoginProvider = activeLoginProviderCount > 1

  return (
    <div className="max-w-3xl flex flex-col space-y-5">
      <Card>
        <Card.Header
          title={$t({ defaultMessage: 'Email', id: 'Generics.Email' })}
        />
        <Card.Content>
          <FormControl.Toggle
            name="email"
            checked={!hideDefaultAuthenticationForm}
            label={$t({
              defaultMessage: 'Login & register with email',
              id: 'Admin.Auth.EmailLogin',
            })}
            helperText={$t({
              defaultMessage:
                'Allow users to register and access the community with an email and password.',
              id: 'Admin.Auth.EmailLogin.Description',
            })}
            disabled={
              !canSafelyDisableLoginProvider && !hideDefaultAuthenticationForm
            }
            onChange={value =>
              updateNetwork({
                input: { hideDefaultAuthenticationForm: !value },
              })
            }
          />
        </Card.Content>
      </Card>
      <Card>
        <Card.Header
          title={$t({
            defaultMessage: 'Social login',
            id: 'Admin.Auth.SocialLogin',
          })}
          description={
            <>
              <T
                defaultMessage="Allow users to register and access the community with social accounts."
                id="Admin.Auth.SocialLogin.Description"
              />{' '}
              <Link
                href="http://bettermode.com/hub/apps-integrations/post/how-to-enable-social-login-for-my-community-on-bettermode-KwoC46SNrETAt0R"
                target="_blank"
                rel="noopener noreferrer"
              >
                <T defaultMessage="Learn more" id="Generics.LearnMore" />
              </Link>
            </>
          }
        />
        <Card.Content>
          <List spacing="lg">
            {defaultSsos?.map(sso => {
              const iconName = icons[sso.type.toLocaleLowerCase()]
              return (
                <List.Item key={sso.type}>
                  <FormControl.Toggle
                    name={sso.type}
                    checked={sso.status === SsoStatus.enable}
                    disabled={
                      !canSafelyDisableLoginProvider &&
                      sso.status === SsoStatus.enable
                    }
                    label={
                      <div className="flex space-s-3 items-center">
                        {iconName && (
                          <SvgSocialIcon name={iconName} size="lg" rounded />
                        )}
                        <span>
                          <T
                            defaultMessage="Login with {sso_provider}"
                            id="Admin.Auth.LoginWithSsoProvider"
                            values={{
                              sso_provider: sso.name,
                            }}
                          />
                        </span>
                      </div>
                    }
                    onChange={value => {
                      updateDefaultSsoStatus({
                        sso: sso.type as unknown as DefaultSsoType,
                        status: value ? SsoStatus.enable : SsoStatus.disable,
                      })
                      completeStep({
                        step: 'login-methods',
                      })
                    }}
                  />
                </List.Item>
              )
            })}
          </List>
        </Card.Content>
      </Card>
      <Oauth2Sso
        disabled={
          oauth2Sso?.status === SsoStatus.enable &&
          !canSafelyDisableLoginProvider
        }
      />
      <JwtSso disabled={jwtSsoActive && !canSafelyDisableLoginProvider} />
      <SamlSso
        disabled={
          samlSso?.status === SsoStatus.enable && !canSafelyDisableLoginProvider
        }
      />
    </div>
  )
}
